import { type Component, createEffect, createMemo, createResource, createSignal, on, Show } from 'solid-js'
import { createStore, produce, reconcile } from 'solid-js/store'
import { z } from 'zod'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Tabs from '~/components/Tabs'
import TagInput from '~/components/TagInput'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompany } from '~/contexts/CompanyContext'
import { useForm } from '~/hooks/useForm'
import { useIsMobile } from '~/hooks/useIsMobile'
import FichaModal from '~/layers/FichaModal'
import TarifaModal from '~/layers/TarifaModal'
import AttributeModal from '~/layers/ui/admin/productos/AttributeModal'
import ContenidoTab from '~/layers/ui/admin/productos/ContenidoTab'
import FichaTab from '~/layers/ui/admin/productos/FichaTab'
import GeneralTab from '~/layers/ui/admin/productos/GeneralTab'
import InventarioTab from '~/layers/ui/admin/productos/InventarioTab'
import MediosTab from '~/layers/ui/admin/productos/MediosTab'
import PreciosTab from '~/layers/ui/admin/productos/PreciosTab'
import StepModal from '~/layers/ui/admin/productos/StepModal'
import VariantesTab from '~/layers/ui/admin/productos/VariantesTab'
import VariantModal from '~/layers/ui/admin/productos/VariantModal'
import { parameterService } from '~/services/parameter.service'
import {
  buildCombinations,
  type InventoryMovementType,
  type MovementTypeType,
  PRODUCT_KIND_OPTIONS,
  PRODUCT_STATUS_OPTIONS,
  type ProductFacetGroupType,
  type ProductKindType,
  type ProductType,
  productService,
  sameOptions,
  toNumber
} from '~/services/product.service'
import type { DraftSpecType, DraftTariffType, DraftType, DraftVariantType } from '~/types/ProductForm'
import { resolveError } from '~/utils/error'
import { generateSlug, slugify } from '~/utils/slug'

/** Categorías del catálogo: parámetros de este tipo, gestionados en Parámetros. */
export const CATEGORY_PARAMETER_TYPE = 'categoria_producto'

const MAX_IMAGE_BYTES = 1_048_576
const DEFAULT_TARIFF = 'Público'

const money = (value: unknown) => `$${toNumber(value).toLocaleString('es-CO')}`

/** Arma el borrador editable a partir de lo que devuelve la API. */
const toDraft = (product?: ProductType | null): DraftType => {
  const generalPrices = (product?.prices ?? []).filter(p => p.variantId == null)

  const tariffs: DraftTariffType[] = []
  for (const price of generalPrices) {
    let tariff = tariffs.find(t => t.name === price.name)
    if (!tariff) {
      tariff = { name: price.name, isDefault: Boolean(price.isDefault), tiers: [] }
      tariffs.push(tariff)
    }
    if (price.isDefault) tariff.isDefault = true
    tariff.tiers.push({ minQuantity: toNumber(price.minQuantity) || 1, amount: toNumber(price.amount) })
  }

  for (const tariff of tariffs) {
    tariff.tiers.sort((a, b) => a.minQuantity - b.minQuantity)
  }

  // Un producto sin precios cargados arranca con la tarifa base y su precio.
  if (tariffs.length === 0) {
    tariffs.push({ name: DEFAULT_TARIFF, isDefault: true, tiers: [{ minQuantity: 1, amount: toNumber(product?.price ?? 0) }] })
  }
  if (!tariffs.some(t => t.isDefault)) tariffs[0].isDefault = true

  const variants: DraftVariantType[] = (product?.variants ?? []).map(v => {
    const overrides: Record<string, number | null> = {}
    for (const price of v.prices ?? []) {
      overrides[price.name] = toNumber(price.amount)
    }
    // Compatibilidad con el precio suelto que tenían las variantes antes de las tarifas.
    if (Object.keys(overrides).length === 0 && v.price != null) {
      overrides[tariffs.find(t => t.isDefault)?.name ?? DEFAULT_TARIFF] = toNumber(v.price)
    }

    return {
      id: v.id,
      sku: v.sku ?? '',
      options: v.options ?? {},
      minStock: toNumber(v.minStock ?? 0),
      active: v.active !== false,
      stock: toNumber(v.stock ?? 0),
      overrides
    }
  })

  return {
    attributes: (product?.attributes ?? []).map(a => ({ name: a.name, values: [...(a.values ?? [])] })),
    specs: (product?.specs ?? []).map(s => ({ label: s.label, value: s.value, unit: s.unit ?? '' })),
    steps: (product?.steps ?? []).map(s => ({ title: s.title, description: s.description ?? '' })),
    variants,
    tariffs
  }
}

export interface ProductFormPropsType {
  /** Nulo al crear. Con producto, la ficha edita. */
  product?: ProductType | null
  initialTab?: string
  /** Se llama al cambiar de pestaña, para que la página la sincronice en la URL. */
  onTabChange?: (tab: string) => void
  onSaved: (product: ProductType) => void
}

/** Pestañas posibles de la ficha; sirve para sanear lo que llegue por la URL. */
const KNOWN_TABS = ['general', 'contenido', 'ficha', 'precios', 'medios', 'variantes', 'inventario'] as const
const sanitizeTab = (tab: string): string => (KNOWN_TABS.includes(tab as (typeof KNOWN_TABS)[number]) ? tab : 'general')

/**
 * Ficha de un elemento del catálogo, la misma para crear y para editar. Está en
 * `layers/` porque la comparten `servicios/nuevo.tsx` y `servicios/[id].tsx`.
 *
 * Los campos sueltos van por `useForm`, pero las listas —atributos, variantes y
 * tarifas— viven en un `createStore`: escribir en `variants[3].sku` sólo cambia
 * ese nodo, sin rehacer la lista. Con señales, cada pulsación creaba un array
 * nuevo y se repintaba todo (y se perdía el foco).
 *
 * Las pestañas sólo pintan su contenido cuando están activas: montar las cinco
 * de golpe crearía el DOM de todas para enseñar una.
 */
const ProductForm: Component<ProductFormPropsType> = props => {
  const toast = useToast()
  const [activeTab, setActiveTab] = createSignal(sanitizeTab(props.initialTab ?? 'general'))
  const [draft, setDraft] = createStore<DraftType>(toDraft(props.product))
  /** Qué tarifa se está editando en la tabla de variantes. */
  const [editingTariff, setEditingTariff] = createSignal(draft.tariffs.find(t => t.isDefault)?.name ?? DEFAULT_TARIFF)
  const { currentCompany } = useCompany()
  const isMobile = useIsMobile()

  /** URL pública de la ficha (catálogo del sitio), para previsualizar como lo ve el cliente. */
  const publicUrl = () => {
    const key = props.product?.key
    const slug = currentCompany().slug
    if (!key || !slug) return ''
    return `/catalogo/${slug}/${key}`
  }

  const alert = useAlert()
  const [showTariffModal, setShowTariffModal] = createSignal(false)
  const [editingTariffIndex, setEditingTariffIndex] = createSignal<number | null>(null)

  // ── Modal de categoría ──────────────────────────────────────────────────────
  const [showCategoryModal, setShowCategoryModal] = createSignal(false)

  const isEdit = () => Boolean(props.product?.id)

  // El enlace «Existencias» del listado apunta a la ficha con ?tab=inventario.
  // Si ya se está en ella, la página no se remonta: hay que atender al cambio.
  createEffect(
    on(
      () => props.initialTab,
      tab => {
        if (tab) setActiveTab(sanitizeTab(tab))
      },
      { defer: true }
    )
  )

  // Al llegar (o recargarse) el producto, el borrador se reconcilia en vez de
  // recrearse: así no parpadea la lista ni se pierde lo que se está editando.
  createEffect(
    on(
      () => props.product,
      product => {
        setDraft(reconcile(toDraft(product), { merge: true }))
        setEditingTariff(draft.tariffs.find(t => t.isDefault)?.name ?? DEFAULT_TARIFF)
      },
      { defer: true }
    )
  )

  const [categories, { mutate: mutateCategories }] = createResource(async () => {
    try {
      const result = await parameterService.list({ type: CATEGORY_PARAMETER_TYPE, limit: 200 })
      return result.items
    } catch {
      return []
    }
  })

  const categoryOptions = () => [{ value: '', title: 'Sin categoría' }, ...(categories() ?? []).map(c => ({ value: String(c.id), title: c.label }))]

  const categorySchema = z.object({
    key: z.string().trim().min(1, 'La clave es obligatoria'),
    label: z.string().trim().min(1, 'La etiqueta es obligatoria')
  })

  const categoryForm = useForm({
    schema: categorySchema,
    initialValues: () => ({ key: '', label: '' }),
    onSubmit: async values => {
      const created = await parameterService.create({
        type: CATEGORY_PARAMETER_TYPE,
        key: values.key,
        label: values.label,
        order: 0
      })
      toast.addToast({ title: 'Categoría creada', type: 'success' })
      setShowCategoryModal(false)
      categoryForm.reset()
      const result = await parameterService.list({ type: CATEGORY_PARAMETER_TYPE, limit: 200 })
      mutateCategories(result.items)
      form.setValue('categoryId', String(created.id))
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const productSchema = z.object({
    kind: z.enum(['service', 'product']).default('product'),
    name: z.string().trim().min(1, 'El nombre es obligatorio'),
    description: z.string().trim().default(''),
    categoryId: z
      .union([z.literal(''), z.coerce.number()])
      .default('')
      .transform(value => (value === '' ? null : Number(value))),
    sku: z.string().trim().default(''),
    barcode: z.string().trim().default(''),
    status: z.enum(['draft', 'published', 'archived']).default('published'),
    cost: z
      .union([z.literal(''), z.coerce.number().min(0, 'No puede ser negativo')])
      .default('')
      .transform(value => (value === '' ? null : Number(value))),
    compareAtPrice: z
      .union([z.literal(''), z.coerce.number().min(0, 'No puede ser negativo')])
      .default('')
      .transform(value => (value === '' ? null : Number(value))),
    taxRate: z.coerce.number().min(0, 'No puede ser negativo').max(100, 'Máximo 100').default(0),
    // `default` sólo cubre el campo ausente; vacío se resuelve aquí.
    unit: z
      .string()
      .trim()
      .default('unidad')
      .transform(value => value || 'unidad'),
    trackInventory: z.boolean().default(false),
    minStock: z.coerce.number().min(0, 'No puede ser negativo').default(0),
    images: z.array(z.string()).default([]),
    tags: z.array(z.string()).default([]),
    details: z.array(z.string()).default([])
  })

  const form = useForm({
    schema: productSchema,
    initialValues: () => {
      const p = props.product
      return {
        kind: p?.kind ?? 'product',
        name: p?.name ?? '',
        description: p?.description ?? '',
        categoryId: p?.categoryId != null ? String(p.categoryId) : '',
        sku: p?.sku ?? '',
        barcode: p?.barcode ?? '',
        status: p?.status ?? 'published',
        cost: p?.cost != null ? String(toNumber(p.cost)) : '',
        compareAtPrice: p?.compareAtPrice != null ? String(toNumber(p.compareAtPrice)) : '',
        taxRate: String(toNumber(p?.taxRate ?? 0)),
        unit: p?.unit ?? 'unidad',
        trackInventory: p?.trackInventory ?? false,
        minStock: String(toNumber(p?.minStock ?? 0)),
        images: p?.images ?? [],
        tags: p?.tags ?? [],
        details: p?.details ?? []
      }
    },
    onSubmit: async values => {
      const invalid = draft.tariffs.find(t => !t.name.trim())
      if (invalid) {
        setActiveTab('precios')
        toast.addToast({ title: 'Hay una tarifa sin nombre', type: 'warning' })
        return
      }

      const payload = {
        ...values,
        description: values.description || null,
        sku: values.sku || null,
        barcode: values.barcode || null,
        price: basePrice(),
        attributes: draft.attributes.filter(a => a.name.trim()).map((a, index) => ({ name: a.name.trim(), values: [...a.values], order: index })),
        // Sin `key` ni `valueSlug`: los calcula el backend, que es lo que hace que
        // "Color" y " COLOR " acaben bajo la misma característica.
        specs: draft.specs.filter(s => s.label.trim() && s.value.trim()).map(s => ({ label: s.label.trim(), value: s.value.trim(), unit: s.unit.trim() || null })),
        // El backend vuelve a sanear el HTML: aquí sólo se decide si hay algo.
        content: content().trim() || null,
        steps: draft.steps.filter(s => s.title.trim()).map(s => ({ title: s.title.trim(), description: s.description.trim() || null })),
        prices: draft.tariffs.flatMap(tariff =>
          tariff.tiers.map((tier, index) => ({
            name: tariff.name.trim(),
            minQuantity: tier.minQuantity,
            amount: tier.amount,
            isDefault: tariff.isDefault,
            order: index
          }))
        ),
        variants: draft.variants.map((variant, index) => ({
          id: variant.id,
          sku: variant.sku || null,
          options: { ...variant.options },
          minStock: variant.minStock,
          active: variant.active,
          order: index,
          // Sólo las tarifas donde esta variante se sale del precio general.
          prices: Object.entries(variant.overrides)
            .filter(([, amount]) => amount != null)
            .map(([name, amount]) => ({ name, minQuantity: 1, amount: amount as number }))
        }))
      }

      const saved = isEdit() ? await productService.update(props.product!.id, payload) : await productService.create(payload)

      toast.addToast({ title: isEdit() ? 'Guardado' : 'Creado', type: 'success' })
      props.onSaved(saved)
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const kind = () => (form.values().kind as ProductKindType) ?? 'product'
  // Memo: devuelve un booleano, así que sólo avisa cuando de verdad cambia de tipo.
  const isService = createMemo(() => kind() === 'service')
  const images = () => (form.values().images as string[]) ?? []
  const tracksInventory = () => Boolean(form.values().trackInventory)

  /** Precio de referencia: tramo base de la tarifa por defecto. */
  const basePrice = () => {
    const tariff = draft.tariffs.find(t => t.isDefault) ?? draft.tariffs[0]
    return tariff?.tiers.find(t => t.minQuantity <= 1)?.amount ?? tariff?.tiers[0]?.amount ?? 0
  }

  const tariffNames = createMemo(() => draft.tariffs.map(t => t.name))

  // ── Medios ──────────────────────────────────────────────────────────────────

  /** Las imágenes se guardan incrustadas: no hay almacenamiento de ficheros. */
  const handleImages = (files: FileList | null) => {
    if (!files || files.length === 0) return

    for (const file of Array.from(files)) {
      if (file.size > MAX_IMAGE_BYTES) {
        toast.addToast({ title: `«${file.name}» supera 1 MB`, type: 'warning' })
        continue
      }
      const reader = new FileReader()
      reader.onload = () => form.setValue('images', [...images(), String(reader.result ?? '')])
      reader.onerror = () => toast.addToast({ title: `No pudimos leer «${file.name}»`, type: 'danger' })
      reader.readAsDataURL(file)
    }
  }

  const removeImage = (index: number) =>
    form.setValue(
      'images',
      images().filter((_, i) => i !== index)
    )

  const moveImageFirst = (index: number) => {
    const list = [...images()]
    const [picked] = list.splice(index, 1)
    form.setValue('images', [picked, ...list])
  }

  // ── Tarifas ─────────────────────────────────────────────────────────────────

  const openCreateTariff = () => {
    setEditingTariffIndex(null)
    setShowTariffModal(true)
  }

  const openEditTariff = (index: number) => {
    setEditingTariffIndex(index)
    setShowTariffModal(true)
  }

  const saveTariff = (tariff: DraftTariffType) => {
    const idx = editingTariffIndex()
    if (idx !== null) {
      // Editar: actualizar la tarifa existente
      const previous = draft.tariffs[idx].name
      setDraft(
        produce(state => {
          state.tariffs[idx].name = tariff.name
          state.tariffs[idx].isDefault = tariff.isDefault
          state.tariffs[idx].tiers = tariff.tiers
          // Renombrar arrastra las excepciones de las variantes
          if (previous !== tariff.name) {
            for (const variant of state.variants) {
              if (previous in variant.overrides) {
                variant.overrides[tariff.name] = variant.overrides[previous]
                delete variant.overrides[previous]
              }
            }
          }
        })
      )
      if (editingTariff() === previous) setEditingTariff(tariff.name)
    } else {
      // Crear: añadir nueva tarifa
      setDraft('tariffs', draft.tariffs.length, tariff)
      setEditingTariff(tariff.name)
    }
    setShowTariffModal(false)
  }

  const removeTariff = async (index: number) => {
    if (draft.tariffs.length === 1) {
      toast.addToast({ title: 'Tiene que quedar al menos una tarifa', type: 'warning' })
      return
    }

    const ok = await alert.confirmDelete({
      title: 'Quitar tarifa',
      message: `¿Quitar la tarifa "${draft.tariffs[index].name}"? Esta acción no se puede deshacer.`
    })
    if (!ok) return

    const removed = draft.tariffs[index].name
    setDraft(
      produce(state => {
        const wasDefault = state.tariffs[index].isDefault
        state.tariffs.splice(index, 1)
        if (wasDefault && state.tariffs[0]) state.tariffs[0].isDefault = true
        for (const variant of state.variants) delete variant.overrides[removed]
      })
    )
    if (editingTariff() === removed) setEditingTariff(draft.tariffs[0]?.name ?? DEFAULT_TARIFF)
  }

  // ── Contenido ───────────────────────────────────────────────────────────────

  /**
   * El HTML del editor va en una señal y no en `useForm`: los valores del
   * formulario se rehacen en cada tecla y el editor perdería el cursor. Se
   * inicializa desde el producto y se sincroniza cuando llega.
   */
  const [content, setContent] = createSignal(props.product?.content ?? '')

  createEffect(
    on(
      () => props.product?.content,
      value => setContent(value ?? ''),
      { defer: true }
    )
  )

  const removeStep = async (index: number) => {
    const ok = await alert.confirmDelete({
      title: 'Quitar paso',
      message: 'Este paso de «Cómo trabajamos» se eliminará de la ficha.'
    })
    if (!ok) return
    setDraft(
      'steps',
      produce(list => {
        list.splice(index, 1)
      })
    )
  }

  const moveStep = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= draft.steps.length) return
    setDraft(
      'steps',
      produce(list => {
        const [row] = list.splice(index, 1)
        list.splice(target, 0, row)
      })
    )
  }

  // ── Modal de pasos ("Cómo trabajamos") ─────────────────────────────────────
  const [showStepModal, setShowStepModal] = createSignal(false)
  const [editingStepIndex, setEditingStepIndex] = createSignal<number | null>(null)

  const openCreateStep = () => {
    setEditingStepIndex(null)
    setShowStepModal(true)
  }

  const openEditStep = (index: number) => {
    setEditingStepIndex(index)
    setShowStepModal(true)
  }

  const saveStep = (step: { title: string; description: string }) => {
    const index = editingStepIndex()
    if (index == null) setDraft('steps', draft.steps.length, step)
    else setDraft('steps', index, step)
    setShowStepModal(false)
  }

  // ── Ficha técnica ───────────────────────────────────────────────────────────

  /**
   * Características ya usadas en la empresa. Se piden una vez y sirven para
   * sugerir al escribir: es lo que evita que nazcan "Color", "Colores" y "color"
   * como tres características distintas, sin obligar a mantener un catálogo.
   */
  const [knownFacets] = createResource(async () => {
    try {
      return await productService.facets()
    } catch {
      // Sin sugerencias se sigue pudiendo escribir; no vale la pena molestar.
      return [] as ProductFacetGroupType[]
    }
  })

  const [showSpecModal, setShowSpecModal] = createSignal(false)
  const [editingSpecIndex, setEditingSpecIndex] = createSignal<number | null>(null)

  const openCreateSpec = () => {
    setEditingSpecIndex(null)
    setShowSpecModal(true)
  }

  const openEditSpec = (index: number) => {
    setEditingSpecIndex(index)
    setShowSpecModal(true)
  }

  const removeSpec = async (index: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar característica',
      message: 'Se quitará esta característica de la ficha técnica.'
    })
    if (!ok) return
    setDraft(
      'specs',
      produce(list => {
        list.splice(index, 1)
      })
    )
  }

  const saveSpec = (spec: DraftSpecType) => {
    const index = editingSpecIndex()
    if (index == null) setDraft('specs', draft.specs.length, spec)
    else setDraft('specs', index, spec)
    setShowSpecModal(false)
  }

  /** Claves ya usadas en esta ficha, para que el modal avise del duplicado. */
  const usedSpecKeys = createMemo(() => draft.specs.map(s => slugify(s.label)).filter(Boolean))

  // ── Atributos y variantes ───────────────────────────────────────────────────

  const removeAttribute = async (index: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar atributo',
      message: 'Se quitará este atributo y sus valores.'
    })
    if (!ok) return
    setDraft(
      'attributes',
      produce(list => {
        list.splice(index, 1)
      })
    )
  }

  // ── Modal de atributos ─────────────────────────────────────────────────────
  const [showAttributeModal, setShowAttributeModal] = createSignal(false)
  const [editingAttributeIndex, setEditingAttributeIndex] = createSignal<number | null>(null)

  const openCreateAttribute = () => {
    setEditingAttributeIndex(null)
    setShowAttributeModal(true)
  }

  const openEditAttribute = (index: number) => {
    setEditingAttributeIndex(index)
    setShowAttributeModal(true)
  }

  const saveAttribute = (attribute: { name: string; values: string[] }) => {
    const index = editingAttributeIndex()
    if (index == null) setDraft('attributes', draft.attributes.length, attribute)
    else setDraft('attributes', index, attribute)
    setShowAttributeModal(false)
  }

  const pendingCombinations = createMemo(() => buildCombinations(draft.attributes).filter(combo => !draft.variants.some(v => sameOptions(v.options, combo))))

  /**
   * Añade sólo las combinaciones que faltan: recrear las existentes les quitaría
   * el id y con él sus existencias y su historial.
   */
  const generateVariants = () => {
    const missing = pendingCombinations()
    if (missing.length === 0) {
      toast.addToast({ title: 'Ya están todas las combinaciones', type: 'default' })
      return
    }

    setDraft(
      produce(state => {
        for (const options of missing) {
          state.variants.push({ sku: '', options, minStock: 0, active: true, stock: 0, overrides: {} })
        }
      })
    )
    toast.addToast({ title: `${missing.length} variante(s) añadida(s)`, type: 'success' })
  }

  const removeVariant = async (index: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar variante',
      message: 'Se quitará esta variante. Si tiene historial de inventario, se archivará en vez de borrarse.'
    })
    if (!ok) return
    setDraft(
      'variants',
      produce(list => {
        list.splice(index, 1)
      })
    )
  }

  // ── Modal de variantes ──────────────────────────────────────────────────────
  const [showVariantModal, setShowVariantModal] = createSignal(false)
  const [editingVariantIndex, setEditingVariantIndex] = createSignal<number | null>(null)

  const openEditVariant = (index: number) => {
    setEditingVariantIndex(index)
    setShowVariantModal(true)
  }

  const saveVariant = (v: { sku: string; minStock: number; active: boolean; overridePrice: string }) => {
    const index = editingVariantIndex()
    if (index == null) return
    setDraft('variants', index, { sku: v.sku, minStock: v.minStock, active: v.active })
    setDraft('variants', index, 'overrides', editingTariff(), v.overridePrice === '' ? null : Number(v.overridePrice))
    setShowVariantModal(false)
  }

  // ── Inventario ──────────────────────────────────────────────────────────────

  const [movements, { refetch: refetchMovements }] = createResource(
    () => (isEdit() ? props.product!.id : undefined),
    async (id: number) => {
      try {
        return await productService.movements(id)
      } catch {
        return [] as InventoryMovementType[]
      }
    }
  )

  const [movementType, setMovementType] = createSignal<MovementTypeType>('in')
  const [movementQty, setMovementQty] = createSignal('')
  const [movementReason, setMovementReason] = createSignal('')
  const [movementVariant, setMovementVariant] = createSignal('')
  const [savingMovement, setSavingMovement] = createSignal(false)

  const savedVariants = () => (props.product?.variants ?? []).filter(v => v.id)

  const registerMovement = async () => {
    const quantity = Number(movementQty())
    if (!Number.isFinite(quantity) || quantity === 0) {
      toast.addToast({ title: 'Indica una cantidad distinta de cero', type: 'warning' })
      return
    }
    if (savedVariants().length > 0 && !movementVariant()) {
      toast.addToast({ title: 'Elige a qué variante afecta', type: 'warning' })
      return
    }

    setSavingMovement(true)
    try {
      await productService.registerMovement(props.product!.id, {
        type: movementType(),
        quantity,
        variantId: movementVariant() ? Number(movementVariant()) : null,
        reason: movementReason() || null
      })
      toast.addToast({ title: 'Movimiento registrado', type: 'success' })
      setMovementQty('')
      setMovementReason('')
      await refetchMovements()
      props.onSaved(await productService.get(props.product!.id))
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setSavingMovement(false)
    }
  }

  const stockTotal = () => toNumber(props.product?.stockTotal ?? props.product?.stock ?? 0)

  /**
   * Los contadores van en memos propios para que la lista de pestañas sólo se
   * rehaga cuando cambia un número. Leyendo `images()` directamente, el memo se
   * recalculaba con cada tecla —los valores del formulario son un objeto nuevo—
   * y `Tabs` repintaba sus botones y sus iconos mientras se escribía.
   */
  const imageCount = createMemo(() => images().length)
  const tariffCount = createMemo(() => draft.tariffs.length)
  const variantCount = createMemo(() => draft.variants.length)

  // La ficha técnica va fuera del ternario de `isService()`: un servicio también
  // tiene características ("duración: 2 h", "modalidad: remoto").
  const tabs = createMemo(() => [
    { id: 'general', label: 'General', icon: 'material-symbols-light:info-outline' },
    { id: 'contenido', label: 'Contenido', icon: 'material-symbols-light:article-outline' },
    { id: 'ficha', label: 'Ficha técnica', icon: 'material-symbols-light:list-alt-outline' },
    { id: 'precios', label: 'Precios', icon: 'material-symbols-light:sell-outline', badge: tariffCount() > 1 ? tariffCount() : undefined },
    { id: 'medios', label: 'Medios', icon: 'material-symbols-light:image-outline', badge: imageCount() || undefined },
    ...(isService()
      ? []
      : [
          { id: 'variantes', label: 'Variantes', icon: 'material-symbols-light:category-outline', badge: variantCount() || undefined },
          { id: 'inventario', label: 'Inventario', icon: 'material-symbols-light:warehouse-outline' }
        ])
  ])

  return (
    <>
      <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 class="text-2xl font-bold text-foreground">{isEdit() ? props.product!.name : 'Nuevo elemento del catálogo'}</h1>
          <p class="text-sm text-foreground-subtle">
            <Show when={isEdit()} fallback="Rellena la ficha; podrás completar variantes y existencias en cuanto lo crees.">
              {isService() ? 'Servicio' : 'Producto'} · clave <span class="font-mono">{props.product!.key}</span>
            </Show>
          </p>
        </div>
        <Button.Group>
          <Show when={isEdit() && publicUrl()}>
            <Button
              type="button"
              variant="soft"
              color="info"
              onClick={() => window.open(publicUrl(), '_blank')}
              disabled={form.submitting()}
              prependIcon="material-symbols-light:open-in-new"
              tooltip="Abrir la ficha pública en una pestaña nueva, como la ve un cliente"
            >
              Ver página
            </Button>
          </Show>
          <Button type="submit" form="product-form" disabled={form.submitting() || !form.valid()} prependIcon="material-symbols-light:save-outline">
            {form.submitting() ? 'Guardando...' : isEdit() ? 'Guardar cambios' : 'Crear'}
          </Button>
        </Button.Group>
      </div>

      <Form id="product-form" controller={form}>
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
          <div class="space-y-6">
            <Card>
              <Card.Header>Estado y tipo</Card.Header>
              <Card.Body>
                <div class="space-y-4">
                  <div>
                    <Select name="kind" label="Tipo" options={PRODUCT_KIND_OPTIONS} value={kind()} />
                    <p class="text-xs text-foreground-subtle mt-1">Un servicio no lleva existencias ni variantes</p>
                  </div>
                  <Select name="status" label="Estado" options={PRODUCT_STATUS_OPTIONS} value={(form.values().status as string) ?? 'published'} />
                  <div>
                    <Select
                      name="categoryId"
                      label="Categoría"
                      options={categoryOptions()}
                      value={String(form.values().categoryId ?? '')}
                      endIcon={
                        <Button
                          type="button"
                          size="xs"
                          variant="text"
                          color="success"
                          icon="material-symbols-light:add"
                          tooltip="Nueva categoría"
                          iconSize={30}
                          onClick={(e: MouseEvent) => {
                            e.stopPropagation()
                            setShowCategoryModal(true)
                          }}
                        />
                      }
                    />
                    <p class="text-xs text-foreground-subtle mt-1">Las categorías se crean en Parámetros, con tipo categoria_producto</p>
                  </div>
                  <TagInput name="tags" label="Etiquetas" placeholder="Escribe y pulsa Enter" helperText="Para buscar y agrupar" />
                </div>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>Precio de referencia</Card.Header>
              <Card.Body>
                <p class="text-3xl font-bold text-foreground">{money(basePrice())}</p>
                <p class="text-xs text-foreground-subtle">Tarifa {draft.tariffs.find(t => t.isDefault)?.name ?? DEFAULT_TARIFF} · es el que se propone al cotizar</p>
              </Card.Body>
            </Card>

            <Show when={isEdit() && props.product!.trackInventory}>
              <Card>
                <Card.Header>Existencias</Card.Header>
                <Card.Body>
                  <div class="flex items-center justify-between">
                    <div>
                      <p class={`text-3xl font-bold ${props.product!.lowStock ? 'text-danger' : 'text-foreground'}`}>{stockTotal().toLocaleString('es-CO')}</p>
                      <p class="text-xs text-foreground-subtle">{props.product!.unit}</p>
                    </div>
                    <Icon
                      icon={props.product!.lowStock ? 'material-symbols-light:warning-outline' : 'material-symbols-light:check-circle-outline'}
                      size={32}
                      class={props.product!.lowStock ? 'text-danger' : 'text-success'}
                    />
                  </div>
                  <Show when={props.product!.lowStock}>
                    <p class="text-xs text-danger mt-2">Por debajo del mínimo</p>
                  </Show>
                </Card.Body>
              </Card>
            </Show>
          </div>

          <div class="xl:col-span-2">
            <Card>
              <Card.Body>
                <Tabs
                  items={tabs()}
                  activeTab={activeTab()}
                  onChange={tab => {
                    setActiveTab(tab)
                    props.onTabChange?.(tab)
                  }}
                  variant="underline"
                />

                <div class="pt-5">
                  {/* Los tabs se mantienen montados y se ocultan con CSS: `useForm`
                      lee el valor de cada campo del DOM, así que desmontar un tab al
                      cambiar dejaría de recibir lo que se había escrito. */}
                  <div class={activeTab() === 'general' ? '' : 'hidden'}>
                    <GeneralTab isService={isService} />
                  </div>

                  <div class={activeTab() === 'contenido' ? '' : 'hidden'}>
                    <ContenidoTab
                      content={content}
                      setContent={setContent}
                      steps={draft.steps}
                      openCreateStep={openCreateStep}
                      openEditStep={openEditStep}
                      moveStep={moveStep}
                      removeStep={removeStep}
                    />
                  </div>

                  <div class={activeTab() === 'ficha' ? '' : 'hidden'}>
                    <FichaTab specs={draft.specs} isMobile={isMobile} isService={isService} openCreateSpec={openCreateSpec} openEditSpec={openEditSpec} removeSpec={removeSpec} />
                  </div>

                  <div class={activeTab() === 'precios' ? '' : 'hidden'}>
                    <PreciosTab
                      tariffs={draft.tariffs}
                      form={form}
                      basePrice={basePrice}
                      money={money}
                      isService={isService}
                      openCreateTariff={openCreateTariff}
                      openEditTariff={openEditTariff}
                      removeTariff={removeTariff}
                    />
                  </div>

                  <div class={activeTab() === 'medios' ? '' : 'hidden'}>
                    <MediosTab images={images} handleImages={handleImages} removeImage={removeImage} moveImageFirst={moveImageFirst} />
                  </div>

                  <div class={activeTab() === 'variantes' ? '' : 'hidden'}>
                    <VariantesTab
                      attributes={draft.attributes}
                      variants={draft.variants}
                      isMobile={isMobile}
                      tariffNames={tariffNames()}
                      editingTariff={editingTariff}
                      setEditingTariff={setEditingTariff}
                      hasMultipleTariffs={draft.tariffs.length > 1}
                      pendingCombinations={pendingCombinations}
                      generateVariants={generateVariants}
                      openCreateAttribute={openCreateAttribute}
                      openEditAttribute={openEditAttribute}
                      removeAttribute={removeAttribute}
                      openEditVariant={openEditVariant}
                      removeVariant={removeVariant}
                    />
                  </div>

                  <div class={activeTab() === 'inventario' ? '' : 'hidden'}>
                    <InventarioTab
                      tracksInventory={tracksInventory}
                      isEdit={isEdit}
                      isMobile={isMobile}
                      savedVariants={savedVariants}
                      movementType={movementType}
                      setMovementType={setMovementType}
                      movementVariant={movementVariant}
                      setMovementVariant={setMovementVariant}
                      movementQty={movementQty}
                      setMovementQty={setMovementQty}
                      movementReason={movementReason}
                      setMovementReason={setMovementReason}
                      savingMovement={savingMovement}
                      registerMovement={registerMovement}
                      movements={movements}
                      movementsLoading={movements.loading}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </Form>

      <TarifaModal
        isOpen={showTariffModal()}
        onClose={() => setShowTariffModal(false)}
        tariff={editingTariffIndex() !== null ? draft.tariffs[editingTariffIndex()!] : null}
        defaultTariffName={DEFAULT_TARIFF}
        onSave={saveTariff}
      />

      <FichaModal
        isOpen={showSpecModal()}
        onClose={() => setShowSpecModal(false)}
        spec={editingSpecIndex() !== null ? draft.specs[editingSpecIndex()!] : null}
        suggestions={knownFacets() ?? []}
        usedKeys={usedSpecKeys()}
        onSave={saveSpec}
      />

      <StepModal isOpen={showStepModal()} onClose={() => setShowStepModal(false)} step={editingStepIndex() !== null ? draft.steps[editingStepIndex()!] : null} onSave={saveStep} />

      <AttributeModal
        isOpen={showAttributeModal()}
        onClose={() => setShowAttributeModal(false)}
        attribute={editingAttributeIndex() !== null ? draft.attributes[editingAttributeIndex()!] : null}
        onSave={saveAttribute}
      />

      <VariantModal
        isOpen={showVariantModal()}
        onClose={() => setShowVariantModal(false)}
        variant={editingVariantIndex() !== null ? draft.variants[editingVariantIndex()!] : null}
        tariffName={editingTariff()}
        onSave={saveVariant}
      />

      <Modal isOpen={showCategoryModal()} onClose={() => setShowCategoryModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">Nueva Categoría</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="category-form" controller={categoryForm}>
            <div class="space-y-4">
              <Input
                name="key"
                label="Clave"
                placeholder="ej: calzado"
                endIcon={
                  <Button
                    size="sm"
                    variant="text"
                    icon="material-symbols-light:autorenew"
                    onClick={() => categoryForm.setValue('key', generateSlug(String(categoryForm.values().label ?? '')))}
                    tooltip="Generar clave desde la etiqueta"
                  />
                }
              />
              <Input name="label" label="Etiqueta" placeholder="ej: Calzado" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowCategoryModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={categoryForm.submitting() || !categoryForm.valid()} form="category-form">
              {categoryForm.submitting() ? 'Guardando...' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default ProductForm
